import type {
  StockAdjustmentMovementType,
  StockAlertType,
  StockReceiptReferenceType,
} from './types';

// NOTE: every `label` below is an i18next KEY (e.g. 'inventory:options.movementType.receipt'),
// not literal display text — consumers must render `t(option.label)`. Translations live in
// public/locales/{lng}/inventory.json under the `options` key. Mirrors catalog/model/options.ts.

export interface SelectOption {
  value: string;
  /** i18next key — render with `t(label)`. */
  label: string;
}

/** i18next keys — render with `t(MOVEMENT_TYPE_LABELS[type])`. Covers the full ledger vocabulary. */
export const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  RECEIPT: 'inventory:options.movementType.receipt',
  ADJUSTMENT_SHRINKAGE: 'inventory:options.movementType.adjustmentShrinkage',
  ADJUSTMENT_DAMAGE: 'inventory:options.movementType.adjustmentDamage',
  ADJUSTMENT_THEFT: 'inventory:options.movementType.adjustmentTheft',
  ADJUSTMENT_FOUND: 'inventory:options.movementType.adjustmentFound',
  ADJUSTMENT_CORRECTION: 'inventory:options.movementType.adjustmentCorrection',
  TRANSFER_OUT: 'inventory:options.movementType.transferOut',
  TRANSFER_IN: 'inventory:options.movementType.transferIn',
  RESERVATION: 'inventory:options.movementType.reservation',
  RESERVATION_RELEASE: 'inventory:options.movementType.reservationRelease',
  RESERVATION_CONSUMED: 'inventory:options.movementType.reservationConsumed',
  ERP_SYNC: 'inventory:options.movementType.erpSync',
};

export const MOVEMENT_TYPE_FILTER_OPTIONS: SelectOption[] = Object.entries(
  MOVEMENT_TYPE_LABELS,
).map(([value, label]) => ({ value, label }));

/** The reason-code select on the Adjustment dialog — only the ADJUSTMENT_* movement types. */
export const STOCK_ADJUSTMENT_REASON_OPTIONS: {
  value: StockAdjustmentMovementType;
  label: string;
}[] = [
  { value: 'ADJUSTMENT_SHRINKAGE', label: 'inventory:options.movementType.adjustmentShrinkage' },
  { value: 'ADJUSTMENT_DAMAGE', label: 'inventory:options.movementType.adjustmentDamage' },
  { value: 'ADJUSTMENT_THEFT', label: 'inventory:options.movementType.adjustmentTheft' },
  { value: 'ADJUSTMENT_FOUND', label: 'inventory:options.movementType.adjustmentFound' },
  { value: 'ADJUSTMENT_CORRECTION', label: 'inventory:options.movementType.adjustmentCorrection' },
];

/** Colour bucket for the ledger's movement-type chip (status = colour + label, never colour alone). */
export function movementTypeColor(
  type?: string,
): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (type) {
    case 'RECEIPT':
    case 'TRANSFER_IN':
    case 'ADJUSTMENT_FOUND':
    case 'RESERVATION_RELEASE':
      return 'success';
    case 'ADJUSTMENT_SHRINKAGE':
    case 'ADJUSTMENT_DAMAGE':
    case 'ADJUSTMENT_THEFT':
    case 'TRANSFER_OUT':
    case 'RESERVATION_CONSUMED':
      return 'error';
    case 'ADJUSTMENT_CORRECTION':
    case 'ERP_SYNC':
      return 'info';
    case 'RESERVATION':
      return 'warning';
    default:
      return 'default';
  }
}

/** i18next keys — render with `t(REFERENCE_TYPE_LABELS[type])`. Covers the full ledger vocabulary. */
export const REFERENCE_TYPE_LABELS: Record<string, string> = {
  ORDER: 'inventory:options.referenceType.order',
  BASKET: 'inventory:options.referenceType.basket',
  TRANSFER: 'inventory:options.referenceType.transfer',
  ERP_IMPORT: 'inventory:options.referenceType.erpImport',
  MANUAL: 'inventory:options.referenceType.manual',
  STOCKTAKE: 'inventory:options.referenceType.stocktake',
  PURCHASE_ORDER: 'inventory:options.referenceType.purchaseOrder',
};

export const REFERENCE_TYPE_FILTER_OPTIONS: SelectOption[] = Object.entries(
  REFERENCE_TYPE_LABELS,
).map(([value, label]) => ({ value, label }));

/** The reference-type select on the Receive dialog — only MANUAL/PURCHASE_ORDER are meaningful. */
export const RECEIPT_REFERENCE_TYPE_OPTIONS: { value: StockReceiptReferenceType; label: string }[] =
  [
    { value: 'MANUAL', label: 'inventory:options.referenceType.manual' },
    { value: 'PURCHASE_ORDER', label: 'inventory:options.referenceType.purchaseOrder' },
  ];

/** i18next keys — render with `t(ACTOR_TYPE_LABELS[type])`. */
export const ACTOR_TYPE_LABELS: Record<string, string> = {
  CUSTOMER: 'inventory:options.actorType.customer',
  MERCHANT_USER: 'inventory:options.actorType.merchantUser',
  SYSTEM: 'inventory:options.actorType.system',
};

/** i18next keys — render with `t(STOCK_ALERT_TYPE_LABELS[type])`. */
export const STOCK_ALERT_TYPE_LABELS: Record<StockAlertType, string> = {
  LOW_STOCK: 'inventory:options.alertType.lowStock',
  OUT_OF_STOCK: 'inventory:options.alertType.outOfStock',
};

/** Colour bucket for the alert-type chip (status = colour + label, never colour alone). */
export function stockAlertTypeColor(type?: string): 'warning' | 'error' | 'default' {
  switch (type) {
    case 'LOW_STOCK':
      return 'warning';
    case 'OUT_OF_STOCK':
      return 'error';
    default:
      return 'default';
  }
}

/**
 * Unit-of-measure allow-list, mirroring the BFF's documented (non-enum, but explicitly
 * recognized) codes for `StockPolicyUpsertRequest.uom`: "EA, PC, KG, G, L, ML, M, CM, DZ, PAIR,
 * SET, PACK, BOX, LB, OZ, FT — a small, extensible, market-neutral allow-list."
 */
export const UOM_OPTIONS: SelectOption[] = [
  { value: 'EA', label: 'inventory:options.uom.EA' },
  { value: 'PC', label: 'inventory:options.uom.PC' },
  { value: 'KG', label: 'inventory:options.uom.KG' },
  { value: 'G', label: 'inventory:options.uom.G' },
  { value: 'L', label: 'inventory:options.uom.L' },
  { value: 'ML', label: 'inventory:options.uom.ML' },
  { value: 'M', label: 'inventory:options.uom.M' },
  { value: 'CM', label: 'inventory:options.uom.CM' },
  { value: 'DZ', label: 'inventory:options.uom.DZ' },
  { value: 'PAIR', label: 'inventory:options.uom.PAIR' },
  { value: 'SET', label: 'inventory:options.uom.SET' },
  { value: 'PACK', label: 'inventory:options.uom.PACK' },
  { value: 'BOX', label: 'inventory:options.uom.BOX' },
  { value: 'LB', label: 'inventory:options.uom.LB' },
  { value: 'OZ', label: 'inventory:options.uom.OZ' },
  { value: 'FT', label: 'inventory:options.uom.FT' },
];
